import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { Button, ButtonGroup } from 'react-bootstrap';
import { connect } from 'react-redux';
import cloudinary from 'cloudinary-core';

import flags from 'flag-icon-css/css/flag-icon.min.css';

import CodeSampleContent from '../CodeSample/CodeSampleContent';

import CdnMap from './CdnMap';
import DemoImage from '../DemoImage';
import UploadResult from '../UploadResult';
import OptimizedImage from '../OptimizedImage';
import * as DemoImageActions from '../../actions/DemoImageActions';
import { applicationModes } from '../../constants';
import * as Consts from '../../constants';
import CdnProvidersTable from './CdnProvidersTable';
import CloudinaryImage from '../CloudinaryImage';

import { defaultImageFormat } from './MultiCdnUtils';

const cl = cloudinary.Cloudinary.new();
cl.fromDocument();

const noop = () => undefined;

const buttonSize = 'small';
const modes = [
    {
        text: 'Original image',
        transformation: { secure: true, alt: 'Original Image', crop: 'scale', width: '600', imageStyle: { maxWidth: '100%' } },
        transformationForUrlPreview: { secure: true, alt: 'Original Image' }
    },
    {
        text: 'Cropped image',
        transformation: { secure: true, crop: 'fill', gravity: 'auto', width: 300, height: 200 }
    },
    {
        text: 'Adaptive format',
        transformation: { secure: true, fetch_format: 'auto', width: 450, crop: 'fit', imageStyle: { maxWidth: '100%' } }
    },
    {
        text: 'Adaptive DPR',
        transformation: { secure: true, dpr: 'auto', crop: 'fill', width: 300, effect: 'red:60' }
    }
];

class MultiCdn extends Component {

    constructor(props) {
        super(props);
        this.state = { mode: modes[0] };
        this.updatePreviewImage = this.updatePreviewImage.bind(this);
        this.setMode = this.setMode.bind(this);
    }

    isImageSelected(props) {
        const { currentImage } = props;
        return !!currentImage.public_id;
    }

    setMode(mode) {
        this.setState({
            mode,
            currentImageSize: undefined,
            currentImageType: undefined
        });
    }

    updatePreviewImage(publicId, size, type) {
        this.setState({
            currentImageSize: size,
            currentImageType: type
        });
    }

    renderTransformationButtons() {
        return (
            <div className="text-center transformation-buttons">
                <ButtonGroup>
                    {modes.map((mode, i) =>
                        <Button
                            key={i}
                            active={this.state.mode === mode}
                            className="transformation-button"
                            bsSize={buttonSize}
                            bsStyle="info"
                            onClick={() => this.setMode(mode)}>
                            {mode.text}
                        </Button>
                    )}
                </ButtonGroup>
            </div>
        );
    }

    renderCurrentImageSize() {
        const { currentImageSize, currentImageType } = this.state;
        if (currentImageSize > 0) {
            return (
                <h4 className="text-center text-uppercase">
                    {Math.round((currentImageSize / 1000))}KB {currentImageType}
                </h4>
            );
        }
        return false;
    }

    headForSize(imageUrl) {
        fetch(imageUrl + '?bust', { method: 'HEAD' }).then((response) => {
            let size = parseInt(response.headers.get('Content-Length'));
            let type = /image\/(.*)/.exec(response.headers.get('Content-Type'))[1].toUpperCase();
            this.updatePreviewImage(this.props.currentImage.public_id, size, type);
        });
    }

    updateImageMetadata(props, state) {
        if (this.isImageSelected(props)) {
            if (state.mode.transformationForUrlPreview) {
                // Displaying a derived image with a transformation but showing properties of the original image
                this.headForSize(cl.url(props.currentImage.public_id, { transformation: state.mode.transformationForUrlPreview }));
            }
        }
    }

    componentWillUpdate(nextProps, nextState) {
        this.updateImageMetadata(nextProps, nextState);
    }

    renderCurrentImage() {
        const transformation = this.state.mode.transformationForUrlPreview ?
            this.state.mode.transformationForUrlPreview : this.state.mode.transformation;
        const { currentImage } = this.props;

        return (
            <section className="well panel-generate" >
                <div className="tab-content text-center">
                    {/* Displays the transformation of the presented image, unless it has transformations which are hidden to the user
                    e.g. this is an original image but we're showing it with w_600 */}
                    <CodeSampleContent
                        transformation={transformation}
                        format={defaultImageFormat}
                        publicId={currentImage.public_id}
                        displayDpr
                        selected
                        key="url"
                        name="url"
                        language="url"
                        cloudName={Consts.CLOUD_NAME} />
                </div>
                <div>
                    {/* The transformation to render might be different than the one presented in the code sample */}
                    <DemoImage
                        publicId={currentImage.public_id}
                        anchorUrl={cl.url(currentImage.public_id, { transformation, format: defaultImageFormat })}
                        id={currentImage.public_id}
                        transformation={{ ...this.state.mode.transformation }}
                                        format={defaultImageFormat}
                    imageStyle={this.state.mode.transformation.imageStyle}
                    className="img-holder text-center cdn-image-preview"
                                        showButton={false}
                    reportImageSize={this.state.mode.transformationForUrlPreview ? noop : this.updatePreviewImage}
                    showDetails={noop}
                    />
                {this.renderCurrentImageSize()}
                </div>
            </section>
        );
    }

    shouldComponentUpdate(nextProps, nextState) {
        const isSameImage = this.props.currentImage.public_id === nextProps.currentImage.public_id;
        const isSameMode = this.state.mode === nextState.mode;
        const isSameImageDetails = this.state.currentImageSize === nextState.currentImageSize &&
            this.state.currentImageType === nextState.currentImageType;
        return !(isSameImage && isSameMode && isSameImageDetails);
    }

    render() {
        if (!this.props.currentImage.public_id) {
            // Nothing to render until an image is selected / uploaded
            return false;
        }
        const transformation = this.state.mode.transformationForUrlPreview ?
            { ...this.state.mode.transformationForUrlPreview } :
            { ...this.state.mode.transformation };
        return (
            <div className="multi-cdn">
                <section className="container">
                    <header className="container heading">
                        <div className="text-center multi-cdn-results-title">
                            <h2>Multi-CDN results</h2>
                            <h5>Dynamic CDN switching: the same dynamic URL is used to transparently deliver your images & videos via different CDNs.</h5>
                        </div>
                    </header>
                    <div className="row">
                        {this.renderTransformationButtons()}
                    </div>
                    <div className="row img-comparison">
                        <div className="col">
                            {this.renderCurrentImage()}
                        </div>
                    </div>
                    <div className="row cdn-providers">
                        <CdnProvidersTable image={this.props.currentImage} transformation={transformation} />
                    </div>
                    <div className="row cdn-visual-results-section">
                        <div className="col-sm-6 cdn-visual-results-container">
                            <h5 className="text-center cdn-visual-results">Results on world map</h5>
                            <CdnMap image={this.props.currentImage} transformation={transformation} />
                        </div>
                        <div className="col-sm-6 cdn-visual-results-container">
                            <h5 className="text-center cdn-visual-results">Radar - Live internet performance map by Cedexis</h5>
                            <a href="http://live.cedexis.com/" target="_blank">
                                <CloudinaryImage
                                    publicId="https://s3-eu-west-1.amazonaws.com/cdx-website-wp-media/wp-content/uploads/20160610223222/radar-live-animation.gif"
                                    type="fetch"
                                    containerStyle={{ width: '80%', height: 'auto', padding: 0, border: 0, margin: 'auto' }}
                                    imageClassName="img-responsive cld-responsive"
                                    cloud_name="demo" // idemo is restricted
                                    transformation={{
                                        width: 'auto',
                                        crop: 'scale'
                                    }}
                                    />
                            </a>
                        </div>
                    </div>
                    <div className="row cdn-footer">
                        <div className="col-sm-12 text-center cdn-footer-title">
                            <h4>Powerful CDN delivery capabilities across multiple CDN solutions</h4>
                        </div>
                        <div className="col-sm-6 cdn-footer-content">
                            <ul>
                                <li>On-the-fly image manipulations via dynamic URLs</li>
                                <li>Adaptive format adaptation for any browser</li>
                                <li>Automatic image optimization with 'Save Data' support</li>
                                <li>SEO-friendly URLs with dynamic suffix</li>
                                <li>Automatic width and DPR using Client Hints</li>
                                <li>Transparent and automatic invalidations</li>
                                <li>Custom domain (CNAME), SSL support, HTTP/2 support</li>
                                <li>Video streaming with on-the-fly transcoding</li>
                            </ul>
                        </div>
                        <div className="col-sm-6 cdn-footer-image">
                            <DemoImage
                                publicId="https://res.cloudinary.com/cloudinary/image/upload/dpr_auto/console_reports.jpg"
                                cloud_name="demo" // idemo is restricted
                                type="fetch"
                                anchorUrl="https://cloudinary.com/signup"
                                id="cloudinary-console"
                                transformation={{ width: 'auto', dpr: 'auto', crop: 'scale' }}
                                format={defaultImageFormat}
                                imageStyle={{ width: '80%', height: 'auto', padding: 0, border: 0, margin: 'auto' }}
                                className="img-holder text-center"
                                showButton={false}
                                showDetails={noop}
                                />
                        </div>
                    </div>
                </section>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        currentImage: state.currentImage
    };
}

export default connect(
    mapStateToProps
)(MultiCdn);



/** WEBPACK FOOTER **
 ** ./src/js/components/MultiCdn/MultiCdn.js
 **/